import { useEffect, useState } from "react";
import { sortByName } from "@/lib/listSort";
import React from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Save, Search, ArrowRight, ChevronDown, ChevronRight, Check, Trash2, KeyRound, Eye, EyeOff, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  super_admin: { label: "Super Admin", color: "#CCFF00", desc: "App owner — every permission below" },
  admin: { label: "Admin", color: "#FF4500", desc: "Approvals, pricing, features" },
  coach: { label: "Coach", color: "#007AFF", desc: "Publishes offerings, rates own players" },
  student: { label: "Student", color: "#A3A3A3", desc: "Books sessions, tracks own progress" },
};

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [userSort, setUserSort] = useState("last");
  const [pending, setPending] = useState({}); // id -> permissions[]
  const [expanded, setExpanded] = useState({}); // id -> bool (show granular)
  const [showTest, setShowTest] = useState(false);
  const [testCount, setTestCount] = useState(0);
  const [purging, setPurging] = useState(false);
  const [selected, setSelected] = useState(new Set()); // bulk-delete selection
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkVisBusy, setBulkVisBusy] = useState(false);

  const load = async () => {
    try {
      const [u, c, t] = await Promise.all([
        api.get(`/admin/users${showTest ? "?include_test=1" : ""}`),
        api.get("/admin/permissions-catalog"),
        api.get("/admin/test-accounts/count"),
      ]);
      setUsers(u.data);
      setCatalog(c.data);
      setTestCount(t.data?.count || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load users");
    }
  };
  useEffect(() => { load(); }, [showTest]);

  const togglePerm = (uid, perm) => {
    setPending((p) => {
      const current = p[uid] || users.find((u) => u.id === uid)?.permissions || [];
      const next = current.includes(perm) ? current.filter((x) => x !== perm) : [...current, perm];
      return { ...p, [uid]: next };
    });
  };

  const save = async (uid) => {
    const perms = pending[uid];
    if (!perms) return;
    try {
      await api.put(`/admin/users/${uid}/permissions`, { permissions: perms });
      toast.success("Permissions updated");
      setPending((p) => { const c = { ...p }; delete c[uid]; return c; });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleCoachVisibility = async (u) => {
    // Iter108c — Optimistic inline flip so the icon reacts instantly, then
    // revert on failure. Uses the iter108b admin endpoint so the audit-log
    // trail stays consistent with the profile-page toggle.
    const nextVisible = u.is_visible_to_public === false;
    setUsers((list) => list.map((row) => row.id === u.id ? { ...row, is_visible_to_public: nextVisible } : row));
    try {
      await api.put(`/admin/users/${u.id}/coach-visibility`, { is_visible_to_public: nextVisible });
      toast.success(nextVisible
        ? `${u.name || "Coach"} is now visible to players`
        : `${u.name || "Coach"} hidden from the public directory`);
    } catch (e) {
      // rollback
      setUsers((list) => list.map((row) => row.id === u.id ? { ...row, is_visible_to_public: !nextVisible } : row));
      toast.error(formatApiError(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const bulkSetVisibility = async (nextVisible) => {
    // Iter108d — Bulk-flip. Loops the same endpoint client-side so audit-log
    // entries stay 1:1 with individual toggles. Reports partial-success cleanly.
    const targets = users.filter((u) => selected.has(u.id) && (u.role === "coach" || u.role === "admin"));
    if (targets.length === 0) return;
    const verb = nextVisible ? "show" : "hide";
    if (!window.confirm(`${nextVisible ? "Show" : "Hide"} ${targets.length} coach${targets.length === 1 ? "" : "es"} ${nextVisible ? "in" : "from"} the public directory?\n\n${targets.map((t) => `• ${t.name || t.email}`).join("\n")}`)) return;
    setBulkVisBusy(true);
    // Optimistic update
    const ids = new Set(targets.map((t) => t.id));
    setUsers((list) => list.map((row) => ids.has(row.id) ? { ...row, is_visible_to_public: nextVisible } : row));
    let ok = 0, fail = 0;
    for (const t of targets) {
      try {
        await api.put(`/admin/users/${t.id}/coach-visibility`, { is_visible_to_public: nextVisible });
        ok += 1;
      } catch {
        fail += 1;
        // rollback that one row
        setUsers((list) => list.map((row) => row.id === t.id ? { ...row, is_visible_to_public: !nextVisible } : row));
      }
    }
    setBulkVisBusy(false);
    setSelected(new Set());
    if (fail === 0) {
      toast.success(`${ok} coach${ok === 1 ? "" : "es"} ${nextVisible ? "now visible to" : "hidden from"} players`);
    } else if (ok === 0) {
      toast.error(`All ${fail} update${fail === 1 ? "" : "s"} failed — try again`);
    } else {
      toast.warning(`${ok} updated, ${fail} failed — refresh to reconcile`);
    }
  };

  const resetUserPassword = async (u) => {
    const pw = window.prompt(
      `Set a temporary password for ${u.name} (${u.email}).\n\nMust be at least 8 characters. Share via a secure channel — they should change it on next login.`
    );
    if (pw === null) return;
    if ((pw || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
    const forceChange = window.confirm(
      `Force ${u.name} to set their own password on next login?\n\n• OK = Yes (recommended). They'll be sent straight to the password-change screen and can't access anything else until they pick a new password.\n• Cancel = No. They can keep using the temp password indefinitely.`
    );
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { new_password: pw, force_change: forceChange });
      toast.success(`Password reset for ${u.email}${forceChange ? " · they'll be forced to change on next login" : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };

  const deleteUser = async (u) => {
    // Bin-icon quick-delete: a plain Cancel/OK confirm is enough friction for
    // an entry in a long list. The full "Delete user permanently" button on
    // the user detail page keeps the strict type-DELETE gate.
    if (!window.confirm(`Delete ${u.name} (${u.email}) permanently?\n\nAll their offerings, bookings, ratings, and personal data will be removed.\n\nThis is irreversible.`)) {
      return;
    }
    try {
      const { data: res } = await api.delete(`/admin/users/${u.id}`);
      const total = Object.values(res.cascade_counts || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Deleted ${u.name} · ${total} related records removed`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const purgeTest = async () => {
    if (testCount === 0) { toast.message("No test accounts to purge"); return; }
    const ok = window.confirm(
      `Purge ${testCount} test account${testCount === 1 ? "" : "s"}?\n\nThis permanently deletes every user whose email matches a test/QA pattern (test_*, qa_*, e2e_*, coachdocs_*, migr_*, fixture_*, seed_*, or @rallylab.test / @test.app / @example.* / @mailinator.com) and cascades their offerings, bookings, ratings, and requests. Real accounts (coach@, student@, admin@) are never touched.\n\nThis is irreversible.`
    );
    if (!ok) return;
    setPurging(true);
    try {
      const { data: res } = await api.post("/admin/purge-test-accounts");
      const cascaded = Object.values(res.cascade_counts || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Purged ${res.purged_user_count} test account${res.purged_user_count === 1 ? "" : "s"} · ${cascaded} related records removed`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Purge failed");
    } finally { setPurging(false); }
  };

  const productionWipe = async () => {
    let preview;
    try { preview = (await api.get("/admin/production-wipe/preview")).data; } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Not allowed"); return;
    }
    const total = Object.values(preview.full_wipe || {}).reduce((a, b) => a + b, 0);
    const typed = window.prompt(
      `PRODUCTION DATA WIPE\n\nDeletes ALL festivals, players, helper passes, transactions, audit logs and email logs (${total} records), plus ${preview.test_accounts} @rallylab.app account(s) and everything linked to them.\n\nKept: pricing/tiers, platform settings, Stripe/Resend/Turnstile config, and all other accounts.\n\nThis cannot be undone. Type WIPE to confirm.`
    );
    if (typed !== "WIPE") { if (typed !== null) toast.error("Wipe cancelled — you must type WIPE"); return; }
    setPurging(true);
    try {
      const { data: res } = await api.post("/admin/production-wipe", { confirm: "WIPE" });
      const n = Object.values(res.deleted || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Production wipe complete · ${n} records removed`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Wipe failed");
    } finally { setPurging(false); }
  };

  const toggleSelected = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected).filter((id) => id !== me?.id);
    if (!ids.length) { toast.message("Nothing selected"); return; }
    const a = window.prompt(
      `Delete ${ids.length} selected account${ids.length === 1 ? "" : "s"} permanently?\n\nAll their offerings, bookings, ratings, and personal data will be removed. This is irreversible.\n\nType DELETE to confirm:`
    );
    if (a !== "DELETE") { toast.message("Cancelled"); return; }
    setBulkDeleting(true);
    try {
      const { data: res } = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      const skipped = (res.results || []).filter((r) => !r.ok);
      const cascaded = Object.values(res.cascade_counts || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Deleted ${res.deleted_count} account${res.deleted_count === 1 ? "" : "s"} · ${cascaded} related records removed${skipped.length ? ` · ${skipped.length} skipped` : ""}`);
      if (skipped.length) {
        skipped.slice(0, 3).forEach((r) => toast.message(`Skipped: ${r.reason}`));
      }
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
    } finally { setBulkDeleting(false); }
  };

  const filtered = sortByName(users.filter((u) => {
    const s = q.toLowerCase();
    return !s || u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  }), (u) => u.name, userSort);

  const Tick = ({ on, color }) => (
    <div
      className="w-7 h-7 inline-flex items-center justify-center border-2 transition-all"
      style={on
        ? { background: color, borderColor: color, boxShadow: `0 0 0 2px rgba(0,0,0,0.35)` }
        : { borderColor: "#404040", background: "transparent" }}
    >
      {on && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="fade-up">
        <div className="label-mono mb-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#CCFF00]" /> SUPER ADMIN · USERS &amp; ROLES</div>
        <h1 className="h-display text-5xl tracking-tighter">Access levels.</h1>
        <div className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Tick a role to grant or remove it. Hit <span className="text-[#CCFF00] font-bold">Save</span> on the right of each row to commit. Click <span className="text-white">More</span> to expose <span className="text-white">granular permissions</span> like &quot;approve coaches only&quot; — perfect for delegating a single responsibility without giving full admin.
        </div>
      </div>

      <div className="card-surface p-4 fade-up d1 flex items-center gap-3 flex-wrap">
        <Search className="w-4 h-4 text-[#A3A3A3]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" data-testid="user-search"
          className="flex-1 min-w-[200px] bg-transparent text-sm focus:outline-none" />
        <span className="label-mono">{filtered.length} / {users.length}</span>
        <div className="inline-flex border border-[#262626]" role="radiogroup" aria-label="Sort" data-testid="user-sort">
          {[["last", "Last name"], ["first", "First name"]].map(([m, l]) => (
            <button key={m} type="button" role="radio" aria-checked={userSort === m} onClick={() => setUserSort(m)} data-testid={`user-sort-${m}`}
              className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider ${userSort === m ? "bg-[#CCFF00] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}>{l} A–Z</button>
          ))}
        </div>
        <div className="h-5 w-px bg-[#262626]" />
        <button
          type="button"
          onClick={() => setShowTest((v) => !v)}
          data-testid="toggle-test-accounts"
          className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border transition-colors"
          style={showTest
            ? { borderColor: "#CCFF00", color: "#CCFF00" }
            : { borderColor: "#262626", color: "#A3A3A3" }}
        >
          {showTest ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {showTest ? "Hiding none" : "Hide test accts"}
          <span className="label-mono ml-1 px-1.5 py-0.5 bg-[#141414] text-[10px]" data-testid="test-acct-count">{testCount}</span>
        </button>
        <button
          type="button"
          onClick={purgeTest}
          disabled={purging || testCount === 0}
          data-testid="purge-test-accounts"
          className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#FF4500]"
        >
          <FlaskConical className="w-3.5 h-3.5" /> {purging ? "Purging…" : `Purge ${testCount} test`}
        </button>
        {(me?.permissions || []).includes("super_admin") && (
          <button
            type="button"
            onClick={productionWipe}
            disabled={purging}
            data-testid="production-wipe-btn"
            className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#FF0033] bg-[#2A0008] text-[#FF4D6A] hover:bg-[#FF0033] hover:text-black transition-colors disabled:opacity-30"
          >
            <FlaskConical className="w-3.5 h-3.5" /> Production data wipe
          </button>
        )}
        {selected.size > 0 && (
          <>
            {/* Iter108d — Bulk visibility. Only counts coach/admin rows in
                the current selection since students have no is_visible_to_public
                surface. Buttons only appear when ≥1 selected row is a coach. */}
            {(() => {
              const selectedCoaches = users.filter((u) => selected.has(u.id) && (u.role === "coach" || u.role === "admin"));
              if (selectedCoaches.length === 0) return null;
              return (
                <>
                  <button
                    type="button"
                    onClick={() => bulkSetVisibility(false)}
                    disabled={bulkVisBusy}
                    data-testid="bulk-hide-selected"
                    className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black transition-colors disabled:opacity-40"
                  >
                    <EyeOff className="w-3.5 h-3.5" /> {bulkVisBusy ? "Hiding…" : `Hide ${selectedCoaches.length} from players`}
                  </button>
                  <button
                    type="button"
                    onClick={() => bulkSetVisibility(true)}
                    disabled={bulkVisBusy}
                    data-testid="bulk-show-selected"
                    className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors disabled:opacity-40"
                  >
                    <Eye className="w-3.5 h-3.5" /> {bulkVisBusy ? "Showing…" : `Show ${selectedCoaches.length} to players`}
                  </button>
                </>
              );
            })()}
            <button
              type="button"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-selected"
              className="text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#FF4500] text-black hover:opacity-90 transition-opacity disabled:opacity-50 font-bold"
            >
              <Trash2 className="w-3.5 h-3.5" /> {bulkDeleting ? "Deleting…" : `Delete ${selected.size} selected`}
            </button>
          </>
        )}
      </div>

      <div className="card-surface fade-up d2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#A3A3A3] text-xs uppercase tracking-wider border-b border-[#262626]">
              <th className="py-3 px-3 w-10">
                <input
                  type="checkbox"
                  data-testid="select-all"
                  checked={filtered.length > 0 && filtered.every((u) => u.id === me?.id || selected.has(u.id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const all = new Set(selected);
                      filtered.forEach((u) => { if (u.id !== me?.id) all.add(u.id); });
                      setSelected(all);
                    } else {
                      setSelected(new Set());
                    }
                  }}
                  className="w-4 h-4 accent-[#FF4500] cursor-pointer"
                  title="Select all visible (except yourself)"
                />
              </th>
              <th className="py-3 px-4">User</th>
              {Object.entries(ROLE_META).map(([k, m]) => (
                <th key={k} className="py-3 px-3 text-center min-w-[110px]">
                  <div className="font-semibold" style={{ color: m.color }}>{m.label}</div>
                  <div className="text-[10px] text-[#525252] normal-case font-normal mt-0.5">{m.desc}</div>
                </th>
              ))}
              <th className="py-3 px-4 text-center min-w-[80px]">Granular</th>
              <th className="py-3 px-4 text-right min-w-[90px]">Save</th>
              <th className="py-3 px-4 text-right min-w-[140px]">Account</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const current = pending[u.id] || u.permissions || [];
              const dirty = !!pending[u.id];
              const isSelf = u.id === me?.id;
              const isOpen = !!expanded[u.id];
              const isSel = selected.has(u.id);
              return (
                <React.Fragment key={u.id}>
                  <tr className="border-b border-[#262626] hover:bg-[#141414]" data-testid={`user-row-${u.id}`} style={isSel ? { background: "rgba(255,69,0,0.06)" } : undefined}>
                    <td className="py-3 px-3">
                      <input
                        type="checkbox"
                        checked={isSel}
                        disabled={isSelf}
                        onChange={() => toggleSelected(u.id)}
                        data-testid={`select-${u.id}`}
                        title={isSelf ? "You can't select your own account" : "Select for bulk delete"}
                        className="w-4 h-4 accent-[#FF4500] cursor-pointer disabled:opacity-30"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-semibold">{u.name}{isSelf && <span className="text-[0.6rem] label-mono text-[#CCFF00] ml-2">YOU</span>}</div>
                      <div className="text-xs text-[#A3A3A3]">{u.email}</div>
                      <Link to={`/admin/users/${u.id}`} className="text-[10px] text-[#525252] hover:text-[#CCFF00] uppercase tracking-wider mt-1 inline-flex items-center gap-1" data-testid={`user-link-${u.id}`}>
                        View full profile <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                    {Object.keys(ROLE_META).map((p) => {
                      const on = current.includes(p);
                      const disabled = isSelf && p === "super_admin"; // can't remove own super_admin
                      return (
                        <td key={p} className="py-3 px-3 text-center">
                          <button
                            onClick={() => !disabled && togglePerm(u.id, p)}
                            disabled={disabled}
                            data-testid={`perm-${u.id}-${p}`}
                            title={disabled ? "Cannot remove your own super_admin" : (on ? "Click to revoke" : "Click to grant")}
                            className="inline-block disabled:opacity-30 hover:scale-110 transition-transform"
                          >
                            <Tick on={on} color={ROLE_META[p].color} />
                          </button>
                        </td>
                      );
                    })}
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [u.id]: !e[u.id] }))}
                        data-testid={`expand-${u.id}`}
                        className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1"
                      >
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} More
                      </button>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {dirty && (
                        <button onClick={() => save(u.id)} className="btn-volt px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid={`save-${u.id}`}>
                          <Save className="w-3 h-3" /> Save
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Iter108c — Inline visibility toggle. Only rendered
                            for coaches/admins so support can hide 3-4 in a
                            row without opening each profile. Optimistic
                            update — reverts on failure. */}
                        {(u.role === "coach" || u.role === "admin") && (
                          <button
                            type="button"
                            onClick={() => toggleCoachVisibility(u)}
                            title={u.is_visible_to_public !== false
                              ? `Visible to players — click to hide ${u.name || "coach"}`
                              : `Hidden from players — click to show ${u.name || "coach"}`}
                            data-testid={`toggle-visibility-${u.id}`}
                            className={`p-1.5 border transition-colors ${
                              u.is_visible_to_public !== false
                                ? "border-[#262626] text-[#CCFF00] hover:border-[#FF4500] hover:text-[#FF4500]"
                                : "border-[#FF4500] text-[#FF4500] hover:border-[#CCFF00] hover:text-[#CCFF00]"
                            }`}
                          >
                            {u.is_visible_to_public !== false
                              ? <Eye className="w-3.5 h-3.5" />
                              : <EyeOff className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => resetUserPassword(u)}
                          disabled={isSelf}
                          title={isSelf ? "Use Account → Change password for yourself" : "Reset this user's password"}
                          data-testid={`reset-pw-${u.id}`}
                          className="p-1.5 border border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors disabled:opacity-30 disabled:hover:border-[#262626] disabled:hover:text-[#A3A3A3]"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteUser(u)}
                          disabled={isSelf}
                          title={isSelf ? "You cannot delete your own account" : "Delete this user permanently"}
                          data-testid={`del-user-${u.id}`}
                          className="p-1.5 border border-[#262626] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500] transition-colors disabled:opacity-30 disabled:hover:border-[#262626] disabled:hover:text-[#A3A3A3]"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && catalog && (
                    <tr className="bg-[#0F0F0F] border-b border-[#262626]" data-testid={`granular-row-${u.id}`}>
                      <td colSpan={9} className="py-4 px-6">
                        <div className="label-mono mb-3 text-[#CCFF00]">ALA-CARTE GRANULAR PERMISSIONS</div>
                        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
                          {catalog.granular.map((p) => {
                            const meta = catalog.meta[p];
                            const on = current.includes(p);
                            const impliedBySuper = current.includes("super_admin");
                            const impliedByAdmin = current.includes("admin") && (catalog.implies?.admin || []).includes(p);
                            const implied = impliedBySuper || impliedByAdmin;
                            return (
                              <button key={p} type="button"
                                onClick={() => togglePerm(u.id, p)}
                                data-testid={`gperm-${u.id}-${p}`}
                                className="flex items-start gap-2 text-left p-2.5 border transition-colors"
                                style={(on || implied) ? { borderColor: meta.color, background: "rgba(204,255,0,0.04)" } : { borderColor: "#262626" }}>
                                <Tick on={on || implied} color={meta.color} />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-semibold flex items-center gap-1.5" style={{ color: (on || implied) ? meta.color : "white" }}>
                                    {meta.label}
                                    {implied && !on && <span className="label-mono px-1 py-0.5 text-[8px]" style={{ background: meta.color, color: "#0A0A0A" }}>IMPLIED</span>}
                                  </div>
                                  <div className="text-[10px] text-[#A3A3A3] leading-snug">{meta.desc}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
